import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { api } from '../services/apiService';
import { AlertCircle, CreditCard } from 'lucide-react';
import { RubyGemMark } from './RubyGemMark';

// Multi-product: detect Stripe publishable key by hostname at runtime.
const PRODUCT_STRIPE_KEYS: Record<string, string> = {
  cafemaster: 'pk_test_51ShWAi4ocIcD7WspNC3a9bRxP7pYB0a5lEphICntJbJHzTUA81xiCM2p2A0ypFatRNqUfOA7WDB546hQ0NahnX8T00rqOdYlg1',
  agenticpos: 'pk_test_51TAWhhRHSQfULkenadaUcbcdxBsjHjjoCTzVal0MVVIlB3b80TfZ8Y2sebmlBLAM7JaACkHYFnhegz4OwkPvlBYd003aavtBoI',
};

function resolveStripeKey() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const envKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
  if (envKey && (hostname === 'localhost' || hostname === '127.0.0.1')) return envKey;
  if (hostname.includes('rubytill')) return PRODUCT_STRIPE_KEYS.agenticpos;
  if (envKey) return envKey;
  return PRODUCT_STRIPE_KEYS.cafemaster;
}

/**
 * ADDITIVE named export. The Finance tab's payment-method panel needs the same
 * Stripe instance for the ACH flow (`collectBankAccountForSetup` is a method on
 * it, not an Element), and a second `loadStripe` call with a second copy of
 * `resolveStripeKey` would be two key resolvers free to drift apart. Nothing
 * about the existing default export or its behaviour changes.
 */
export const stripePromise = loadStripe(resolveStripeKey());

interface StripePaymentMethodFormProps {
  tenantId: string;
  onSuccess?: (paymentMethodId: string) => void;
  onClose?: () => void;
}

/**
 * Inner component that uses Stripe hooks
 * Must be wrapped with Elements provider
 */
const PaymentMethodFormContent: React.FC<StripePaymentMethodFormProps> = ({
  tenantId,
  onSuccess,
  onClose,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cardholderName, setCardholderName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Stripe is not loaded. Please refresh the page.');
      return;
    }

    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment method with Stripe
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)!,
        billing_details: {
          name: cardholderName,
        },
      });

      if (stripeError) {
        setError(stripeError.message || 'Failed to process card. Please try again.');
        setLoading(false);
        return;
      }

      if (!paymentMethod) {
        setError('Failed to create payment method. Please try again.');
        setLoading(false);
        return;
      }

      // Stripe has tokenised the card. NOTHING has been stored against the
      // tenant yet — `onSuccess` below is what starts that, and it can fail.
      setSuccess(true);
      // Clear form
      setCardholderName('');
      elements.getElement(CardElement)?.clear();

      // Call success callback after brief delay
      setTimeout(() => {
        if (onSuccess) onSuccess(paymentMethod.id);
        if (onClose) onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Payment method error:', error);
      setError(error.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /*
   * WHAT THIS SCREEN MAY CLAIM.
   *
   * It is shown the moment Stripe returns a payment-method id, and `onSuccess`
   * — the call that actually stores it against the tenant — does not run for
   * another two seconds and can fail. It therefore reports the step that HAS
   * finished, tokenisation, and says the save is still to come. Every caller
   * reports the real outcome itself once its own call returns.
   *
   * It previously read "Payment Method Updated! Your payment method has been
   * successfully updated." — announcing an update that had not been attempted.
   */
  if (success) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <RubyGemMark size={48} drawing />
          </div>
        </div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a1a', marginBottom: '8px' }}>
          Card accepted by Stripe
        </h3>
        <p style={{ color: '#666666', marginBottom: '16px' }}>
          Stripe has accepted the card and returned a reference to it. Saving that against this account
          happens next — nothing is stored until it does, and the result is reported once it finishes.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Cardholder Name */}
      <div style={{ marginBottom: '20px' }}>
        <label
          htmlFor="cardholder"
          style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '8px',
          }}
        >
          Cardholder Name
        </label>
        <input
          id="cardholder"
          type="text"
          placeholder="John Doe"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            fontSize: '14px',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
            opacity: loading ? 0.6 : 1,
            color: '#1a1a1a',
            backgroundColor: '#ffffff',
          }}
        />
      </div>

      {/* Card Element */}
      <div style={{ marginBottom: '20px' }}>
        <label
          htmlFor="card"
          style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#1a1a1a',
            marginBottom: '8px',
          }}
        >
          Card Details
        </label>
        <div
          style={{
            padding: '12px',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            backgroundColor: '#ffffff',
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '14px',
                  color: '#1a1a1a',
                  fontFamily: 'Arial, sans-serif',
                  '::placeholder': {
                    color: '#999999',
                  },
                },
                invalid: {
                  color: '#dc2626',
                },
              },
            }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div
          style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '20px',
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
          }}
        >
          <AlertCircle style={{ width: '18px', height: '18px', color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ color: '#991b1b', fontSize: '14px', margin: 0 }}>{error}</p>
          </div>
        </div>
      )}

      {/* Security Badge */}
      <div
        style={{
          backgroundColor: '#f5f5f5',
          border: '1px solid #e5e5e5',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '20px',
          fontSize: '12px',
          color: '#666666',
          textAlign: 'center',
        }}
      >
        Your payment information is secure and encrypted by Stripe.
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !stripe || !elements}
        style={{
          width: '100%',
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          padding: '14px',
          borderRadius: '10px',
          border: 'none',
          fontWeight: '600',
          fontSize: '14px',
          cursor: loading || !stripe || !elements ? 'not-allowed' : 'pointer',
          opacity: loading || !stripe || !elements ? 0.6 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        {loading ? (
          <>
            <RubyGemMark size={16} drawing color="currentColor" />
            Processing...
          </>
        ) : (
          'Update Payment Method'
        )}
      </button>
    </form>
  );
};

/**
 * Wrapper component with Stripe Elements provider
 */
export const StripePaymentMethodForm: React.FC<StripePaymentMethodFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodFormContent {...props} />
    </Elements>
  );
};

/**
 * Modal wrapper component for payment method update
 */
interface PaymentMethodModalProps {
  isOpen: boolean;
  tenantId: string;
  onClose: () => void;
  onSuccess?: (paymentMethodId: string) => void;
  currentPaymentMethod?: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
}

// Helper to format card brand name
const formatCardBrand = (brand: string): string => {
  const brandNames: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brandNames[brand.toLowerCase()] || brand.charAt(0).toUpperCase() + brand.slice(1);
};

export const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  isOpen,
  tenantId,
  onClose,
  onSuccess,
  currentPaymentMethod,
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '30px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '1px solid #e5e5e5',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '6px' }}>
          Update Payment Method
        </h2>
        <p style={{ color: '#666666', fontSize: '14px', marginBottom: '24px' }}>
          Enter your new card details below. Your current subscription will continue without interruption.
        </p>

        {/* Current Card Info */}
        {currentPaymentMethod && (
          <div
            style={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #e5e5e5',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '24px',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#666666', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Current Card
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CreditCard style={{ width: '24px', height: '24px', color: '#1a1a1a' }} />
              <div>
                <div style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>
                  {formatCardBrand(currentPaymentMethod.brand)} •••• {currentPaymentMethod.last4}
                </div>
                <div style={{ fontSize: '13px', color: '#666666' }}>
                  Expires {String(currentPaymentMethod.expMonth).padStart(2, '0')}/{currentPaymentMethod.expYear}
                </div>
              </div>
            </div>
          </div>
        )}

        <StripePaymentMethodForm
          tenantId={tenantId}
          onSuccess={(paymentMethodId) => {
            if (onSuccess) onSuccess(paymentMethodId);
            setTimeout(onClose, 2000);
          }}
          onClose={onClose}
        />

        <button
          onClick={onClose}
          style={{
            marginTop: '16px',
            width: '100%',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            fontWeight: '600',
            cursor: 'pointer',
            color: '#333333',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default StripePaymentMethodForm;
